import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useTimer } from "use-timer";
import coolGif from "../../../components/images/cool.gif";
import winnerGif from "../../../components/images/winner.gif";
import loseGif from "../../../components/images/you-lose.gif";
import alarmGif from "../../../components/images/alarm-clock.gif";
import { logEvent } from "firebase/analytics";
import analytics from "../../../context/analytics";

// components
import { Box, MainHolder, StyledText } from "../../../components/common";

//svgs
import { NewBackButton } from "../../../components/svgs";
import { useTrivia } from "../../../hooks";
import TriviaInfoModal from "./TriviInfoModal";
import PlayTriviaModal from "./PlayTriviaModal";

const defaultTriviaDuration = 90; // seconds

const TriviaPlay = () => {
  const history = useHistory();
  const { activeTrivia, openPlayTriviaModal } = useTrivia();
  const { gameDuration } = activeTrivia || {};
  const [isReset, resetGame] = useState(false);
  useEffect(() => {
    if (isReset) {
      if (!openPlayTriviaModal) setTimeout(() => { resetGame(false) }, 50)
    }
  }, [isReset, openPlayTriviaModal])


  if (!isReset) {
    return (
      <Game
        resetGame={resetGame}
        gameDuration={gameDuration || defaultTriviaDuration}
      />
    )
  }

  if (openPlayTriviaModal) {
    return <PlayTriviaModal onBackDropClick={() => history.push('/trivia/trivia-category')} />
  }
  
  return <></>
}

const Game = ({ resetGame, gameDuration }) => {
  const { time, start, pause, status } = useTimer({
    initialTime: gameDuration,
    endTime: 0,
    timerType: "DECREMENTAL",
  });

  const history = useHistory();
  const {
    activeTrivia,
    setActiveTrivia,
    getQuestions,
    questions,
    gameId,
    isSubmitting,
    submitAnswer,
    answerResponse,
    setAnswerResponse,
    getTrivia,
    activeTriviaCategory,
    getHistory,
    locationSelected,
    refreshGame,
    trivia,
    openPlayTriviaModal,
    setOpenPlayTriviaModal,
    isLoadingTrivia,
  } = useTrivia();

  const [currentQuestionIndex, setQuestionIndex] = useState(0);

  const [showRules, setShowRules] = useState(false);
  const [modalLossOpen, setModalLossOpen] = useState(false);
  const [modalWonOpen, setModalWonOpen] = useState(false);
  const [modalTimeOpen, setModalTimeOpen] = useState(false);
  const [goToNext, setGoToNext] = useState(false);
  const [ticketCode, setTicketCode] = useState();
  const [attemptsRemaining, setAttemptsRemaining] = useState();

  useEffect(() => {
    if (trivia) {
      const currentTrivia = trivia
        .find((triv) => triv._id === activeTriviaCategory._id)
        ?.trivia?.find((triv) => triv._id === activeTrivia._id);
      setQuestionIndex(0);
      setActiveTrivia(currentTrivia);
    }
  }, [trivia]);

  useEffect(() => {
    getQuestions(activeTrivia?._id);

    return () => {
      // Clean up
      setShowRules(false);
      setModalWonOpen(false);
      setModalLossOpen(false);
      setModalTimeOpen(false);
      setAnswerResponse(null);
    };
  }, []);

  useEffect(() => {
    if (showRules || !questions || isSubmitting) {
      pause();
    } else {
      start();
    }
  }, [isSubmitting, questions, showRules]);

  useEffect(() => {
    if (time === 0 && status === "STOPPED") {
      setModalTimeOpen(true);
      getTrivia();
    }
  }, [time, status]);

  const handleSubmit = (answer) => {
    if (isSubmitting) return;
    // Handle submission here
    const questionId = questions[currentQuestionIndex].questionID;
    const triviaId = activeTrivia._id;
    submitAnswer({ triviaId, gameId, answer, questionId });
    setGoToNext(true);
  };

  const gotoNextQuestion = () => {
    const nextIndex = currentQuestionIndex + 1;
    setQuestionIndex(
      nextIndex > questions.length - 1 ? currentQuestionIndex : nextIndex
    );
  };

  useEffect(() => {
    if (answerResponse) {
      const {
        continue: canContinue,
        isCorrect,
        hasWon,
        ticket,
      } = answerResponse || {};
      if (canContinue && isCorrect) {
        if (!isSubmitting && goToNext) {
          // Move to next
          gotoNextQuestion();
        }
      } else if (hasWon) {
        setTicketCode(ticket?.ticketCode);
        setModalWonOpen(true);
        getTrivia();
        pause();
        analytics().then((anl) =>
          logEvent(anl, "trivia_win", {
            triviaName: activeTrivia?.title,
          })
        );
        getHistory();
      } else {
        setModalLossOpen(true);
        getTrivia();
        pause();
        analytics().then((anl) =>
          logEvent(anl, "trivia_loss", {
            triviaName: activeTrivia?.title,
          })
        );
      }
    }
  }, [isSubmitting, goToNext, answerResponse]);

  useEffect(() => {
    if (activeTrivia) {
      const { maxAttempts, userStats } = activeTrivia;
      const { attempts } = userStats || {};
      setAttemptsRemaining(maxAttempts - attempts);
    }
  }, [activeTrivia]);

  const goToHome = () => {
    const token = JSON.parse(localStorage.getItem("token"));
    history.push(`/home?token=${token}`);
  };

  const lossAction = () => {
    if (attemptsRemaining > 0 && locationSelected) {
      getTrivia();
      setOpenPlayTriviaModal(true);
      resetGame(true);
    } else {
      setOpenPlayTriviaModal(false);
      history.push("/trivia/trivia-category");
    }
  }

  return (
    <>
      <MainHolder
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
        }}
      >
        {modalWonOpen && (
          <WonDialog
            message={ticketCode ? activeTrivia?.wonRewardMessage : activeTrivia?.wonNoRewardMessage}
            action={() => {
              if (ticketCode) {
                history.push("/trivia/pick")
              } else {
                goToHome();
              }
            }}
            ticketCode={ticketCode}
            setModalOpen={setModalWonOpen}
            title='Good Job'
          />
        )}
        {modalLossOpen && (
          <LossDialog
            action={lossAction}
            setModalOpen={setModalLossOpen}
            attemptsRemaining={attemptsRemaining}
          />
        )}
        {modalTimeOpen && (
          <TimeOutDialog
            action={lossAction}
            setModalOpen={setModalTimeOpen}
            attemptsRemaining={attemptsRemaining}
          />
        )}

        {showRules && (
          <Box
            style={{
              position: "absolute",
              top: 69,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "white",
            }}
          >
            <div
              style={{
                position: "relative",
                textAlign: "center",
                maxWidth: "361px",
                margin: "0 auto",
              }}
            >
              <div style={{ padding: "0 10px" }}>
                <StyledText style={{ fontSize: 22, textAlign: "center" }}>
                  Trivia Rules
                </StyledText>
                <img
                  alt="cool"
                  src={coolGif}
                  style={{ width: "98px", margin: "0 auto" }}
                />

                <Box
                  style={{
                    backgroundColor: "white",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <StyledText
                    style={{
                      fontSize: "14px",
                      textAlign: "center",
                      color: "#7F7F7F",
                      fontFamily: "MTNRegular",
                    }}
                  >
                    You have {gameDuration} secs to correctly answer all the
                    questions
                  </StyledText>
                  <StyledText
                    style={{
                      fontSize: "14px",
                      textAlign: "center",
                      color: "#7F7F7F",
                      fontFamily: "MTNRegular",
                    }}
                  >
                    Once you answer a question wrongly, it’s game over for you
                  </StyledText>
                  <StyledText
                    style={{
                      fontSize: "14px",
                      textAlign: "center",
                      color: "#7F7F7F",
                      fontFamily: "MTNRegular",
                    }}
                  >
                    You have {attemptsRemaining} attempt
                    {attemptsRemaining > 1 ? "s" : ""} to play
                  </StyledText>
                  <StyledText
                    style={{
                      fontSize: "12px",
                      textAlign: "center",
                      color: "#7F7F7F",
                      fontFamily: "MTNRegular",
                    }}
                  ></StyledText>
                </Box>
                <StyledText style={{ fontSize: 20, textAlign: "center" }}>
                  Are you ready?
                </StyledText>
              </div>
              <Box
                onClick={() => {
                  setShowRules(false);
                  analytics().then((anl) =>
                    logEvent(anl, "trivia_attempts", {
                      triviaName: activeTrivia?.title,
                    })
                  );
                }}
                style={{
                  margin: 25,
                  marginBottom: 50,
                  padding: 5,
                  borderRadius: 30,
                  backgroundColor: "#E31C79",
                  cursor: "pointer",
                }}
              >
                <StyledText
                  style={{
                    textAlign: "center",
                    color: "white",
                  }}
                >
                  Accept terms and play
                </StyledText>
              </Box>
            </div>
          </Box>
        )}

        <Box
          style={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#EE2737",
            paddingLeft: 15,
            paddingTop: 5,
            paddingBottom: 5,
          }}
        >
          <div style={{ position: "absolute" }}>
            <BackButtonContainer
              onClick={() => {
                getTrivia();
                history.goBack();
              }}
            >
              <NewBackButton />
            </BackButtonContainer>
          </div>
          <Box
            style={{
              display: "flex",
              flex: 1,
              backgroundColor: "#EE2737",
              justifyContent: "center",
            }}
          >
            <StyledText style={{ fontSize: 18, color: "white" }}>
              {status === "STOPPED" ? "Time's Up!" : time || 10}
            </StyledText>
          </Box>
        </Box>
        <StyledText style={{ fontSize: 22 }}>
          {activeTriviaCategory?.name}
        </StyledText>

        <Box
          style={{
            borderWidth: 1.5,
            borderRadius: 10,
            margin: 15,
            backgroundColor: "white",
            border: "solid 1px #7F7F7F",
            padding: 20,
          }}
        >
          <StyledText style={{ textAlign: "center", color: "#7F7F7F" }}>
            Question {currentQuestionIndex + 1} of {questions?.length}
          </StyledText>
          <StyledText style={{ textAlign: "center" }}>
            {questions ? questions[currentQuestionIndex].question : ""}
          </StyledText>
          <Box
            style={{
              borderWidth: 1,
              borderRadius: 10,
              margin: 15,
              border: "solid 1 #7F7F7F",
              backgroundColor: "#7F7F7F",
              paddingTop: 1,
            }}
          />
          <Box style={{ margin: 15, backgroundColor: "white", marginTop: 30 }}>
            <AnswerSelector
              disabled={isSubmitting || isLoadingTrivia}
              handleSubmit={handleSubmit}
              answers={
                questions && questions.length > 0
                  ? questions[currentQuestionIndex].possibleAnswers?.map(
                    (ans) => ans.choice
                  )
                  : []
              }
            />
          </Box>
        </Box>
      </MainHolder>
      {/* <PlayTriviaModal /> */}
    </>
  );
};

export default TriviaPlay;

const Dialog = ({
  title,
  image,
  imageWidth = 200,
  onClose,
  description,
  action,
}) => {
  return (
    <Box
      style={{
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        position: "absolute",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        backgroundColor: "rgba(0,0,0,0.5)",
      }}
    >
      <Box
        style={{
          opacity: 1,
          backgroundColor: "white",
          marginLeft: 15,
          marginRight: 15,
          borderRadius: 30,
          padding: "10px 15px 0 15px",
        }}
      >
        <StyledText style={{ fontSize: 22, textAlign: "center" }}>
          {title}
        </StyledText>
        <div style={{ textAlign: "center" }}>
          <img
            alt={"dialog"}
            src={image}
            style={{ width: `${imageWidth}px`, margin: "0 auto" }}
          />
        </div>
        {description}
        <Box
          onClick={onClose}
          style={{
            margin: 25,
            marginBottom: 50,
            paddingTop: 2,
            paddingBottom: 2,
            borderRadius: 30,
            backgroundColor: "#E31C79",
            cursor: "pointer",
          }}
        >
          <StyledText
            style={{
              textAlign: "center",
              color: "white",
            }}
          >
            Ok
          </StyledText>
        </Box>
      </Box>
    </Box>
  );
};

const WonDialog = ({
  setModalOpen,
  action,
  ticketCode = null,
  title = "You win!",
  message
}) => {
  return (
    <Dialog
      title={title}
      image={winnerGif}
      onClose={() => {
        action();
        setModalOpen(false);
      }}
      description={
        <StyledText
          style={{
            fontSize: "14px",
            textAlign: "center",
            fontFamily: "MTNRegular",
          }}
        >
          <div>{message}</div>
          {ticketCode ? `Reference code: ${ticketCode}` : ""}
        </StyledText>
      }
    />
  );
};


const LossDialog = ({ setModalOpen, action, attemptsRemaining }) => {
  return (
    <Dialog
      title="You lose"
      image={loseGif}
      imageWidth={140}
      onClose={() => {
        action();
        setModalOpen(false);
      }}
      description={
        <StyledText
          style={{
            textAlign: "center",
            fontSize: "14px",
            fontFamily: "MTNRegular",
          }}
        >
          Unfortunately you weren't able to correctly answer all the questions.
        </StyledText>
      }
    />
  );
};

const TimeOutDialog = ({ setModalOpen, action, attemptsRemaining }) => {
  return (
    <Dialog
      title="Time's Up!"
      image={alarmGif}
      imageWidth={200}
      onClose={() => {
        action();
        setModalOpen(false);
      }}
      description={
        <StyledText
          style={{
            fontSize: "14px",
            textAlign: "center",
            fontFamily: "MTNRegular",
          }}
        >
          Unfortunately, you weren't able to answer in time.
        </StyledText>
      }
    />
  );
};

const AnswerSelector = ({ disabled, answers, handleSubmit }) => {
  const [selection, setSelection] = useState();

  const changeSelection = (selected) => {
    if (disabled) return;
    setSelection(selected);
    handleSubmit(selected);
  };

  useEffect(() => {
    if (answers && !disabled) {
      setSelection("");
    }
  }, [answers]);

  return (
    <>
      {answers.map((text, index) => {
        const isSelected = text === selection;

        return (
          <Box
            onClick={() => {
              changeSelection(text);
            }}
            key={index}
            style={{
              opacity: disabled ? 0.4 : 1,
              display: "flex",
              borderWidth: 1,
              borderRadius: 30,
              border: `solid 1.5px ${isSelected ? "#0FAF4B" : "#7F7F7F"}`,
              backgroundColor: isSelected ? "#0FAF4B" : "white",
              paddingLeft: 10,
              alignItems: "center",
              marginBottom: 12,
              cursor: "pointer",
            }}
          >
            <Box
              style={{
                border: `solid 1.5px ${isSelected ? "white" : "#7F7F7F"}`,
                padding: 4,
                borderRadius: 30,
                display: "flex",
                backgroundColor: isSelected ? "#0FAF4B" : "white",
              }}
            >
              <Box
                style={{
                  backgroundColor: isSelected ? "#ffffff" : "#7F7F7F",
                  padding: 6,
                  borderRadius: 30,
                }}
              />
            </Box>
            <StyledText
              style={{
                color: isSelected ? "#ffffff" : "#7F7F7F",
                marginLeft: 8,
              }}
            >
              {text}
            </StyledText>
          </Box>
        );
      })}
    </>
  );
};

const BackButtonContainer = styled.div`
  background-color: #ffcc00;
  width: 30px;
  height: 30px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
